import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';
import LoadingSpinner from '../../../components/LoadingSpinner';
import { ArrowLeft, CaretDown, MagnifyingGlass } from '@phosphor-icons/react';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import { Country, State } from 'country-state-city';

// Custom dropdown component
function SearchableSelect({ options, value, onChange, placeholder, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const filteredOptions = options.filter(option =>
    option.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(opt => opt.value === value);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className={`w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-left flex items-center justify-between ${
          disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'
        }`}
      >
        <span className={selectedOption ? 'text-gray-900' : 'text-gray-400'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <CaretDown size={16} className="text-gray-400" />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-hidden">
            <div className="p-2 border-b border-gray-200">
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search..."
                  className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-48">
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => {
                      onChange(option.value);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className={`w-full px-4 py-2 text-left hover:bg-blue-50 transition-colors ${
                      value === option.value ? 'bg-blue-100 text-blue-900 font-medium' : 'text-gray-900'
                    }`}
                  >
                    {option.label}
                  </button>
                ))
              ) : (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  No results found
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default function GroupLeaderUpdateInformation() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    date_of_birth: '',
    address: '',
    city: '',
    state: '',
    country: '',
    post_code: ''
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadUserData();
  }, []);

  const loadUserData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();
      
      setFormData({
        name: userData.name || '',
        phone: userData.phone || '',
        email: userData.email || '',
        date_of_birth: userData.date_of_birth || '',
        address: userData.address || '',
        city: userData.city || '',
        state: userData.state || '',
        country: userData.country || '',
        post_code: userData.post_code || ''
      });
    } catch (error) {
      console.error('Load user data error:', error);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error: updateError } = await supabase
        .from('users')
        .update({
          name: formData.name,
          phone: formData.phone || null,
          date_of_birth: formData.date_of_birth || null,
          address: formData.address || null,
          city: formData.city || null,
          state: formData.state || null,
          country: formData.country || null,
          post_code: formData.post_code || null
        })
        .eq('id', user.id);

      if (updateError) throw updateError;

      setSuccess('Information updated successfully!');
      setTimeout(() => {
        navigate('/group-leader/profile');
      }, 1500);
    } catch (error) {
      console.error('Update information error:', error);
      setError('Failed to update information. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Get all countries
  const countries = Country.getAllCountries().map(country => ({
    value: country.isoCode,
    label: country.name
  })).sort((a, b) => a.label.localeCompare(b.label));

  // Get states for selected country
  const getStatesForCountry = () => {
    if (!formData.country) return [];
    const countryCode = countries.find(c => c.label === formData.country)?.value;
    if (!countryCode) return [];
    
    const states = State.getStatesOfCountry(countryCode);
    return states.map(state => ({
      value: state.name,
      label: state.name
    })).sort((a, b) => a.label.localeCompare(b.label));
  };

  if (loading) {
    return <LoadingSpinner message="Loading..." />;
  }

  return (
    <div className="bg-gray-50 min-h-screen pb-20">
      {/* Custom CSS for phone input */}
      <style>{`
        .react-international-phone-input-container {
          width: 100%;
          position: relative;
        }

        .react-international-phone-input-container .react-international-phone-input {
          width: 100%;
          height: 48px;
          border: 1px solid #d1d5db;
          border-radius: 12px;
          padding: 12px 16px 12px 76px;
          font-size: 16px;
          outline: none;
          background: white;
        }

        .react-international-phone-input-container .react-international-phone-input:focus {
          border-color: #3b82f6;
          ring: 2px;
          ring-color: #3b82f6;
        }

        .react-international-phone-country-selector-button {
          position: absolute;
          left: 8px;
          top: 50%;
          transform: translateY(-50%);
          border: none;
          background: transparent;
          height: 36px;
          padding: 0 4px;
          display: flex;
          align-items: center;
          gap: 4px;
          cursor: pointer;
          z-index: 1;
        }

        .react-international-phone-country-selector-button:hover {
          background-color: transparent;
        }

        .react-international-phone-country-selector-button__button-content {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .react-international-phone-country-selector-button__flag-emoji {
          font-size: 20px;
        }

        .react-international-phone-country-selector-button__dropdown-arrow {
          display: none !important;
        }

        .react-international-phone-country-selector-dropdown {
          max-height: 300px;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
          margin-top: 4px;
        }

        .react-international-phone-country-selector-dropdown__list-item {
          padding: 8px 12px;
          cursor: pointer;
          transition: background-color 0.15s;
        }

        .react-international-phone-country-selector-dropdown__list-item:hover {
          background-color: #eff6ff;
        }

        .react-international-phone-country-selector-dropdown__list-item--selected {
          background-color: #dbeafe;
          font-weight: 500;
        }

        .react-international-phone-country-selector-dropdown__list-item img {
          width: 24px !important;
          height: 18px !important;
        }
      `}</style>

      {/* Header */}
      <div className="bg-white border-b safe-area-top sticky top-0 z-10">
        <div className="flex items-center px-4 py-4">
          <button
            onClick={() => navigate('/group-leader/profile')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors -ml-2"
          >
            <ArrowLeft size={24} weight="regular" />
          </button>
          <h1 className="text-lg font-bold text-gray-900 absolute left-1/2 transform -translate-x-1/2">
            Update Information
          </h1>
        </div>
      </div>

      {/* Form */}
      <div className="p-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                {error}
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl text-sm">
                {success}
              </div>
            )}

            {/* Basic Information Section */}
            <div className="pb-4 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900 mb-4">Basic Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    value={formData.email}
                    disabled
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-gray-100 text-gray-500 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 mt-1">Email address cannot be changed</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <div className="relative">
                    <PhoneInput
                      defaultCountry="us"
                      value={formData.phone}
                      onChange={(phone) => setFormData({ ...formData, phone })}
                    />
                    <CaretDown 
                      size={12} 
                      weight="bold"
                      className="absolute left-10 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none z-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 max-w-full"
                  />
                </div>
              </div>
            </div>

            {/* Address Section */}
            <div className="pt-4">
              <h3 className="font-semibold text-gray-900 mb-4">Address Information</h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Street Address
                  </label>
                  <input
                    type="text"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="123 Main Street"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="New York"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Country
                  </label>
                  <SearchableSelect
                    options={countries}
                    value={countries.find(c => c.label === formData.country)?.value || ''}
                    onChange={(value) => {
                      const country = countries.find(c => c.value === value);
                      setFormData({ ...formData, country: country?.label || '', state: '' });
                    }}
                    placeholder="Select country"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    State/Province
                  </label>
                  <SearchableSelect
                    options={getStatesForCountry()}
                    value={formData.state}
                    onChange={(value) => setFormData({ ...formData, state: value })}
                    placeholder={formData.country ? "Select state/province" : "Select country first"}
                    disabled={!formData.country || getStatesForCountry().length === 0}
                  />
                  {formData.country && getStatesForCountry().length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">No states available for this country</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    value={formData.post_code}
                    onChange={(e) => setFormData({ ...formData, post_code: e.target.value })}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="10001"
                  />
                </div>
              </div>
            </div>

            <button
              type="submit"
              disabled={saving}
              className="w-full bg-blue-600 text-white px-4 py-3 rounded-xl font-medium hover:bg-blue-700 transition-colors border border-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}